import React from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { InformationPopUp, FormSpacer } from 'components';
import { FormGroupLabel, DatePicker } from '@qbe/ads-core';
import { FormCard, Toggle } from '@qbe/ads-blocks-forms';
import { FormDataType, titleList, genderList } from 'contexts/FormProvider';
import { CustomerType } from '../enum';
import { useContentContext } from '@qbe/dfc-content';
import { DropdownController, FirstnameLastname } from 'controllers';
import dayjs from 'dayjs';
import { useContentComplaintPartners } from 'utils/content';

interface CustomerDetailsProps {
  form: UseFormReturn<FormDataType>;
  isFieldRequired: (fieldName: keyof FormDataType) => boolean;
  customerType: CustomerType | string | null;
  children?: React.ReactNode;
}

const CustomerDetails = ({ form, customerType, children, isFieldRequired }: CustomerDetailsProps) => {
  const { control } = form;

  const { getTextContent } = useContentComplaintPartners({ context: useContentContext() });

  const TEXT_CONTENT = {
    heading: getTextContent('insured.customerDetails.heading'),
    sectionHeading: getTextContent('insured.customerDetails.sectionHeading'),
    individualText: getTextContent('insured.individualOrBusiness.0.text'),
    businessText: getTextContent('insured.individualOrBusiness.1.text'),
    customerTitle: getTextContent('insured.customerDetails.customerTitle'),
    customerTitleLabel: getTextContent('insured.customerDetails.customerTitle.label'),
    customerName: getTextContent('insured.customerDetails.customerName'),
    contactPersonTitle: getTextContent('insured.customerDetails.contactPersonTitle'),
    contactPersonName: getTextContent('insured.customerDetails.contactPersonName'),
    dob: getTextContent('insured.customerDetails.dob'),
    dobInfoText: getTextContent('insured.customerDetails.dob.infoText'),
    gender: getTextContent('insured.customerDetails.gender'),
  };

  const isIndividual = customerType === CustomerType.INDIVIDUAL;
  const isBusiness = customerType === CustomerType.BUSINESS;

  return (
    <>
      <FormCard title={TEXT_CONTENT.heading} variant="sectionTitle">
        <Controller
          name="insuredIndOrBus"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Toggle
              {...field}
              required={isFieldRequired('insuredIndOrBus')}
              title={TEXT_CONTENT.sectionHeading}
              optionList={[
                {
                  text: TEXT_CONTENT.individualText,
                  value: CustomerType.INDIVIDUAL.toString(),
                },
                {
                  text: TEXT_CONTENT.businessText,
                  value: CustomerType.BUSINESS.toString(),
                },
              ].map((option) => ({
                autoComplete: 'off',
                id: option.value,
                value: option.value,
                label: option.text ?? (option as any).value,
                defaultChecked: customerType === option.value,
                'data-test': 'insuredIndOrBus',
                'data-track': 'insuredIndOrBus',
                'data-track-value': 'capture',
              }))}
            />
          )}
        />

        {!isBusiness && (
          <>
            <FormGroupLabel alignCenter title={TEXT_CONTENT.customerTitle} legendElementOveride />
            <FormSpacer type="input" />

            <DropdownController
              control={control}
              name="customerTitle"
              label={TEXT_CONTENT.customerTitleLabel}
              capture
              optionList={titleList.map((label) => ({ label, value: label }))}
            />

            <FirstnameLastname
              form={form}
              maxLength={50}
              slotProps={{
                titleField: {
                  title: `${TEXT_CONTENT.customerName}`,
                },
                firstnameField: {
                  name: 'customerFirstName',
                  label: 'First name',
                },
                lastnameField: {
                  name: 'customerLastName',
                  label: 'Last name',
                },
              }}
            />
          </>
        )}

        {isBusiness && (
          <>
            <FormGroupLabel alignCenter title={TEXT_CONTENT.contactPersonTitle} />
            <FormSpacer type="input" />
            <DropdownController
              control={control}
              name="contactPersonTitle"
              label={TEXT_CONTENT.customerTitleLabel}
              capture
              optionList={titleList.map((label) => ({ label, value: label }))}
            />

            <FirstnameLastname
              form={form}
              maxLength={50}
              slotProps={{
                titleField: {
                  title: TEXT_CONTENT.contactPersonName,
                },
                firstnameField: {
                  name: 'contactFirstName',
                  label: 'Contact first name',
                },
                lastnameField: {
                  name: 'contactLastName',
                  label: 'Contact last name',
                },
              }}
            />
          </>
        )}

        {isIndividual && (
          <>
            <FormGroupLabel
              title={TEXT_CONTENT.dob}
              titleSuffix={<InformationPopUp message={TEXT_CONTENT.dobInfoText} />}
              required
              alignCenter
            />
            <FormSpacer type="input" />
            <Controller
              name="dob"
              control={control}
              render={({ field: { value, ...field }, fieldState: { error } }) => {
                return (
                  <DatePicker
                    {...field}
                    value={dayjs(value as any)}
                    id={field.name}
                    label="DD/MM/YYYY"
                    format="DD/MM/YYYY"
                    data-track={field.name}
                    data-track-value="mask"
                    maxDate={dayjs()}
                    data-test={field.name}
                    error={!!error}
                    helperText={error?.message}
                  />
                );
              }}
            />

            <FormGroupLabel alignCenter title={TEXT_CONTENT.gender} required={isFieldRequired('gender')} />
            <FormSpacer type="input" />
            <DropdownController
              control={control}
              name="gender"
              label={TEXT_CONTENT.gender}
              capture
              optionList={genderList.map((label) => ({ label, value: label }))}
            />
          </>
        )}

        {children}
      </FormCard>
    </>
  );
};

export default CustomerDetails;
